import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Icon } from "@iconify/react/dist/iconify.js";

const TopAttackedUrlsChart = ({ data }) => {
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
  };

  // Format URLs for display (truncate if too long)
  const formatUrl = (url) => {
    return url.length > 20 ? url.substring(0, 17) + '...' : url;
  };

  // Aggregate all daily data to get total attacks per day
  const aggregateDailyData = () => {
    if (!data || data.length === 0) return [];
    
    // Create a map to store aggregated counts by day
    const dailyMap = {};
    
    // Iterate through all URLs and their daily data
    data.forEach(urlData => {
      if (urlData.daily && urlData.daily.length > 0) {
        urlData.daily.forEach(day => {
          if (!dailyMap[day.day]) {
            dailyMap[day.day] = { day: day.day, count: 0 };
          }
          dailyMap[day.day].count += day.count;
        });
      }
    });
    
    // Convert map to array and sort by day
    return Object.values(dailyMap).sort((a, b) => new Date(a.day) - new Date(b.day));
  };
  
  const overviewData = aggregateDailyData();
  
  // Format data for the URL list
  const urlListData = data.map(item => ({
    url: formatUrl(item.url),
    fullUrl: item.url,
    count: item.count
  })).sort((a, b) => b.count - a.count); // Sort for list (descending)

  // Get daily data for selected URL or first URL if none selected
  const getSelectedUrlData = () => {
    if (!data || data.length === 0) return [];
    
    const urlData = selectedUrl 
      ? data.find(item => item.url === selectedUrl)
      : data[0];
      
    return urlData?.daily || [];
  };

  const dailyData = getSelectedUrlData();
  const currentUrl = selectedUrl || (data && data.length > 0 ? data[0].url : 'N/A');
  const displayUrl = formatUrl(currentUrl);

  // Calculate total attacks
  const totalAttacks = data?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;

  return (
    <div 
      className={`
        flex flex-col 
        card-container 
        rounded-md 
        p-2 sm:p-3 
        w-full 
        ${showDetails ? 'h-auto' : 'h-full'}
      `}
    >
      <div className="flex flex-row justify-between items-center mb-2">
        <div className="text-lg font-semibold text-font-primary">
          Top Attacked URLs
        </div>
        <button 
          className="flex items-center gap-1 text-sm text-font-primary hover:text-primary transition-colors p-2 rounded-md button-container"
          onClick={() => setShowDetails(!showDetails)}
        >
          <span>Details</span>
          <Icon 
            icon={showDetails ? "mingcute:up-line" : "mingcute:down-line"} 
            className="text-lg text-primary" 
          />
        </button>
      </div>
      
      <div className="flex flex-row items-center justify-between mb-4">
        <div className="text-sm text-font-secondary">Total Attacks: <span className="font-medium text-primary">{totalAttacks}</span></div>
        {selectedUrl && (
          <div className="text-sm text-font-secondary">Selected URL: <span className="font-medium text-primary" title={currentUrl}>{displayUrl}</span></div>
        )}
      </div>
      
      {/* Details Table */}
      {showDetails && (
        <div className="mb-4 overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="text-left py-2 text-xs text-font-secondary">URL Path</th>
                <th className="text-right py-2 text-xs text-font-secondary">Attack Count</th>
              </tr>
            </thead>
            <tbody>
              {urlListData.map((item, index) => (
                <tr 
                  key={index} 
                  className={`border-t border-bgAccent hover:bg-bgAccent cursor-pointer ${selectedUrl === item.fullUrl ? 'bg-bgAccent' : ''}`}
                  onClick={() => setSelectedUrl(item.fullUrl)}
                  title={item.fullUrl}
                >
                  <td className="py-2 text-sm truncate max-w-[200px]">{item.url}</td>
                  <td className="text-right py-2 text-sm">{item.count}</td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr className="border-t border-gray-200">
                  <td colSpan={2} className="text-center py-4 text-sm">No attacked URLs to display</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Overview horizontal bar chart showing all top URLs */}
        <div className="h-[250px]">
          <div className="flex items-center justify-end mb-2">
            <div className="flex items-center gap-1 text-xs text-font-secondary">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#ffa940' }}></div>
              <span>Attacks by Day</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={overviewData} 
              margin={{ top: 5, right: 0, left: -35, bottom: 25 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                opacity={0.2} 
              />
              <XAxis 
                dataKey="day" 
                tickFormatter={formatDate} 
                stroke="#aaa" 
              />
              <YAxis 
                stroke="#aaa" 
                allowDecimals={false} 
              />
              <Tooltip 
                formatter={(value) => [value, 'Attacks']} 
                labelFormatter={formatDate} 
              />
              <Bar 
                dataKey="count" 
                name="Total Attacks" 
                fill="#ffa940" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Daily trend for selected URL */}
        <div className="h-[250px]">
          <div className="text-sm font-medium mb-2" title={currentUrl}>Daily Activity for URL: {displayUrl}</div>
          <ResponsiveContainer width="100%" height="90%">
            <BarChart 
              data={dailyData}
              margin={{ top: 5, right: 0, left: -35, bottom: 5 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                opacity={0.2} 
              />
              <XAxis 
                dataKey="day" 
                tickFormatter={formatDate} 
                stroke="#aaa" 
              />
              <YAxis 
                stroke="#aaa" 
                allowDecimals={false} 
              />
              <Tooltip 
                formatter={(value) => [value, 'Attacks']} 
                labelFormatter={formatDate} 
              />
              <Bar 
                dataKey="count" 
                name="Attacks" 
                fill="#3189E0" 
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default TopAttackedUrlsChart;